import { TranslationType } from '../types';

export const de: TranslationType = {
  common: {
    wallets: 'Wallets',
    dashboard: 'Dashboard',
    settings: 'Einstellungen',
    darkMode: 'Dunkelmodus',
    lightMode: 'Hellmodus',
    language: 'Sprache',
    english: 'English',
    chinese: '中文',
    russian: 'Русский',
    german: 'Deutsch',
    indonesian: 'Bahasa Indonesia',
    korean: '한국어',
    japanese: '日本語',
    thai: 'ภาษาไทย',
    blog: 'Blog'
  },
  footer: {
    quickLinks: 'Schnellzugriff',
    about: 'Über uns',
    terms: 'Nutzungsbedingungen',
    privacy: 'Datenschutz',
    contact: 'Kontakt',
    copyright: '© 2025 XCH.live. Alle Rechte vorbehalten.',
    description: 'Fortschrittliche Überwachungsplattform für Chia-Blockchain-Wallets. Erhalten Sie Echtzeitbenachrichtigungen für Transaktionen, sichern Sie Ihr Vermögen mit sofortigen Benachrichtigungen und behalten Sie die Kontrolle über Ihre Cold-Wallet-Aktivitäten.',
    connectWithUs: 'Kontaktieren Sie uns',
    builtBy: 'Entwickelt mit ❤️ von Spacescan.io'
  },
  home: {
    hero: {
      title: 'Überwachen Sie Ihre Chia Cold Wallet',
      subtitle: 'Erhalten Sie sofortige E-Mail-Benachrichtigungen für XCH-Transaktionen',
      input: {
        label: 'Geben Sie den Master-Key (96 Zeichen) oder die XCH-Adresse ein',
        placeholder: 'Geben Sie den Master-Key (96 Zeichen) oder die XCH-Adresse ein',
        helper: 'Geben Sie Ihren Master-Key oder Ihre XCH-Adresse ein, um die Überwachung zu starten'
      },
      buttons: {
        cardprefix: '$12',
        card: 'Mit Karte bezahlen',
        strprefix: '50% OFF',
        str: 'Mit STR bezahlen'
      }
    },
    features: {
      title: 'Schützen Sie Ihre Chia, bevor es zu spät ist',
      subtitle: 'Warten Sie nicht auf Hacker — schützen Sie Ihre Cold Wallet proaktiv mit 24/7-Überwachung',
      vault: {
        title: 'Vault & Clawback aktivieren',
        description: 'Sichern Sie Ihr Vermögen mit erweiterten Vault-Funktionen und Clawback-Schutz'
      },
      monitoring: {
        title: 'Sichere Überwachung',
        description: 'Überwachen Sie Ihre Cold-Wallet-Aktivitäten ohne private Schlüssel preiszugeben'
      },
      alerts: {
        title: 'Sofortige Benachrichtigungen',
        description: 'Erhalten Sie sofortige E-Mail-Benachrichtigungen für jede Transaktion'
      },
      recovery: {
        title: 'Clawback-Wiederherstellung',
        description: 'Holen Sie sich Geld von nicht autorisierten Transaktionen innerhalb der Clawback-Frist zurück'
      }
    },
    metrics: {
      title: 'Unsere Wirkung in Zahlen',
      walletsMonitored: {
        label: 'Überwachte Wallets'
      },
      alertsSent: {
        label: 'Gesendete Benachrichtigungen'
      },
      transactionsAnalyzed: {
        label: 'Analysierte Transaktionen'
      }
    },
    testimonials: {
      title: 'Was unsere Nutzer sagen',
      items: [
        {
          quote: 'XCH.live gibt mir die Gewissheit, dass mein Cold Wallet rund um die Uhr überwacht wird. Die sofortigen Benachrichtigungen sind bahnbrechend.',
          author: 'Michael Chen',
          role: 'Chia-Farmer'
        },
        {
          quote: 'Die Einrichtung war unglaublich einfach und die Überwachung ist zuverlässig. Genau das, was ich für meine Chia-Bestände brauchte.',
          author: 'Sarah Johnson',
          role: 'Krypto-Investor'
        },
        {
          quote: 'Die beste Überwachungslösung für Chia-Wallets. Die E-Mail-Benachrichtigungen sind schnell und das Dashboard ist sehr intuitiv.',
          author: 'David Kim',
          role: 'Blockchain-Entwickler'
        }
      ]
    }
  },
  settings: {
    title: 'Einstellungen',
    subtitle: 'Verwalten Sie Ihre Wallet-Einstellungen',
    notifications: {
      title: 'Benachrichtigungen',
      emailNotifications: 'E-Mail-Benachrichtigungen',
      emailDescription: 'Transaktionsupdates per E-Mail erhalten',
      threshold: {
        title: 'Transaktionsschwelle',
        description: 'Mindest-XCH-Betrag für Benachrichtigungen (mindestens 1 XCH)'
      }
    },
    walletInfo: {
      title: 'Wallet-Informationen',
      masterKey: 'Master-Key'
    },
    actions: {
      save: 'Änderungen speichern',
      delete: 'Wallet löschen'
    },
    messages: {
      success: 'Einstellungen erfolgreich gespeichert!',
      error: {
        generic: 'Einstellungen konnten nicht aktualisiert werden',
        threshold: 'Schwelle muss mindestens 1 XCH betragen',
        delete: 'Wallet konnte nicht gelöscht werden'
      },
      deleteConfirm: 'Sind Sie sicher, dass Sie diese Wallet löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
    }
  },
  wallets: {
    title: 'Ihre Wallets',
    subtitle: 'Verwalten und überwachen Sie Ihre Cold Wallets',
    status: {
      active: 'Überwachung aktiv',
      pending: 'Zahlung ausstehend',
      expired: 'Überwachung abgelaufen',
      timeLeft: {
        days: '{{days}}T {{hours}}Std übrig',
        hours: '{{hours}}Std {{minutes}}Min übrig',
        minutes: '{{minutes}}Min übrig',
        lessThanMinute: 'Weniger als 1Min übrig'
      }
    },
    actions: {
      payWithCard: 'Mit Karte bezahlen',
      completeCryptoPayment: 'Krypto-Zahlung abschließen',
      renewMonitoring: 'Überwachung verlängern',
      dashboard: 'Dashboard',
      settings: 'Einstellungen',
      addFirstWallet: 'Erste Wallet hinzufügen',
      removeWallet: 'Wallet entfernen'
    },
    messages: {
      noWallets: 'Keine Wallets gefunden'
    }
  },
  dashboard: {
    title: 'Wallet-Dashboard',
    tabs: {
      events: 'Ereignisse',
      addresses: 'Adressen'
    },
    addresses: {
      totalAddresses: 'Gesamtadressen: {{count}}',
      index: 'Index',
      address: 'Adresse',
      actions: 'Aktionen'
    },
    events: {
      loadMore: 'Mehr laden',
      noEvents: 'Keine Ereignisse gefunden',
      sent: 'Gesendet',
      received: 'Empfangen',
      spent: 'Ausgegeben',
      pending: 'Ausstehend',
      failed: 'Fehlgeschlagen',
      showDetails: 'Details anzeigen',
      hideDetails: 'Details ausblenden',
      block: 'Block',
      viewOnSpacescan: 'Auf Spacescan ansehen',
      transactionHash: 'Transaktions-Hash',
      nftsReceived: 'NFTs empfangen',
      nftsSent: 'NFTs gesendet',
      nftTransaction: 'NFT-Transaktion',
      viewNFTDetails: 'NFT-Details anzeigen',
      receivedNFTs: 'Empfangene NFTs',
      sentNFTs: 'Gesendete NFTs'
    }
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    security: {
      question: 'Wie sicher ist Ihr Überwachungsservice?',
      answer: 'Unser Service arbeitet nach höchsten Sicherheitsstandards und verwendet ausschließlich Ihren öffentlichen Master-Key für Überwachungszwecke. Ihre Gelder bleiben vollständig sicher, da wir niemals Zugriff auf private Schlüssel oder Ausgabemöglichkeiten haben. Wir implementieren BLS-12-381 kryptographische Standards, bei denen private Schlüssel (32 Bytes) und öffentliche Schlüssel (48 Bytes) getrennt und sicher bleiben.',
      learnMore: 'Mehr über BLS-Schlüssel in Chia erfahren →'
    },
    monitoring: {
      question: 'Wie häufig überwachen Sie Transaktionen?',
      answer: 'Unser System überwacht Transaktionen in Echtzeit und prüft jeden neuen Block in der Chia-Blockchain. Wir verfolgen Aktivitäten über alle Ihre überwachten Adressen hinweg und stellen eine sofortige Benachrichtigung bei allen Transaktionen sicher, die Ihr Wallet betreffen.'
    },
    masterKey: {
      question: 'Wie finde ich den öffentlichen Master-Key meines Wallets?',
      answer: 'Auf Ihren öffentlichen Master-Key können Sie entweder über die Chia Wallet GUI oder CLI zugreifen. Wir haben eine umfassende Videoanleitung erstellt, die Sie durch den Prozess führt. Wichtig: Teilen Sie niemals Ihre privaten Schlüssel oder Seed-Phrase - nur der öffentliche Master-Key wird für unseren Überwachungsservice benötigt.',
      videoTitle: 'So finden Sie Ihren öffentlichen Master-Key',
      learnMore: 'Lesen Sie die detaillierte Anleitung zum Auffinden Ihrer Schlüssel →'
    },
    addresses: {
      question: 'Wie viele Adressen werden mit meinem Master-Key überwacht?',
      answer: 'Unser Service überwacht 100 Ableitungsadressen, die mit Ihrem Master-Key verbunden sind, und bietet damit eine umfassende Abdeckung der Transaktionsaktivitäten Ihres Wallets.'
    },
    singleAddress: {
      question: 'Kann ich statt eines Master-Keys eine bestimmte Adresse überwachen?',
      answer: 'Ja, unser Service bietet die Flexibilität, einzelne Adressen zu überwachen. Sie können sich dafür entscheiden, eine einzelne spezifische Adresse anstelle eines Master-Keys für die Überwachung mehrerer Adressen zu verfolgen.'
    },
    notifications: {
      question: 'Wie kann ich meine Benachrichtigungseinstellungen anpassen?',
      answer: 'Sie haben über Ihre Wallet-Einstellungen volle Kontrolle über Ihre E-Mail-Benachrichtigungen. Unser Dashboard bietet Optionen zur Anpassung der Benachrichtigungspräferenzen entsprechend Ihren Überwachungsbedürfnissen.'
    },
    vault: {
      question: 'Was ist ein Chia-Vault und wie funktioniert er?',
      answer: 'Ein Chia-Vault ist eine fortgeschrittene Sicherheitsfunktion, die erweiterten Schutz für Ihre digitalen Vermögenswerte bietet. Er implementiert zusätzliche Sicherheitsmaßnahmen wie Multi-Signatur-Anforderungen oder zeitverzögerte Transaktionen. Vaults trennen die Vermögensverwahrung von Coins und können mehrere Signaturen (m von n Multi-Sig) für die Transaktionsgenehmigung erfordern.',
      learnMore: 'Mehr über Chia-Vaults erfahren →'
    },
    clawback: {
      question: 'Was ist die Clawback-Funktion in Chia?',
      answer: 'Clawback ist eine Sicherheitsfunktion in Chia, die die Umkehrbarkeit von Transaktionen innerhalb eines festgelegten Zeitraums ermöglicht. Wenn aktiviert, müssen Empfänger das Ende der Clawback-Periode abwarten, bevor sie auf die übertragenen Gelder zugreifen können, was eine zusätzliche Ebene der Transaktionssicherheit bietet.'
    },
    vaultSupport: {
      question: 'Unterstützt Ihr Service Vault- und Clawback-Funktionen?',
      answer: 'Während wir Transaktionen mit Vaults und Clawback-Funktionen überwachen, verwalten wir diese Funktionen nicht direkt. Diese Funktionen müssen bei der Wallet-Erstellung konfiguriert werden. Unser Service konzentriert sich auf die Transaktionsüberwachung und Benachrichtigungszustellung für Ihre Adressen.'
    },
    tokens: {
      question: 'Bieten Sie Überwachung für Chia-Token an?',
      answer: 'Derzeit konzentriert sich unser Überwachungsservice ausschließlich auf XCH-Transaktionen. Die Token-Überwachungsfunktionalität wird in unserem aktuellen Serviceangebot nicht unterstützt.'
    },
    nft: {
      question: 'Ist die Überwachung von NFT-Transaktionen verfügbar?',
      answer: 'Gegenwärtig spezialisiert sich unser Service auf die Überwachung von XCH-Transaktionen. Die Überwachung von NFT-Transaktionen ist in unseren aktuellen Servicefunktionen nicht enthalten.'
    },
    subscription: {
      question: 'Was ist Ihre Abonnement- und Rückerstattungsrichtlinie?',
      answer: 'Unser Service wird auf Abonnementbasis angeboten. Aufgrund der unmittelbaren Natur unserer Überwachungsdienste bieten wir keine Rückerstattungen an, sobald der Service aktiviert wurde. Bitte stellen Sie sicher, dass Sie unsere Servicebedingungen verstehen, bevor Sie ein Abonnement abschließen.'
    }
  },
  notFound: {
    title: 'Seite nicht gefunden',
    lostMessage: 'Lassen Sie Ihre Chia nicht wie diese Seite - in der Leere verschwinden!',
    securityMessage: 'Kehren Sie zur Startseite zurück und erfahren Sie, wie Sie Ihre XCH rund um die Uhr sichern & überwachen können.',
    protectButton: 'Schützen Sie Ihre Chia'
  }
}; 